import castErrorToBaseError from '@/functions/castErrorToBaseError';
import updateLockStore from '@/modules/core/stores/update-lock';
import PromotionForm from '@/modules/promotions/components/PromotionForm';
import promotionCreationFormDefaultValues from '@/modules/promotions/constants/promotion-form-default-values';
import usePromotionFormValidationSchema from '@/modules/promotions/hooks/usePromotionFormValidationSchema';
import useCreatePromotionMutation from '@/modules/promotions/mutations/useCreatePromotionMutation';
import type { PromotionCreationFormValues } from '@/modules/promotions/types/PromotionCreationFormValues';
import { Helmet } from '@dr.pogodin/react-helmet';
import { joiResolver } from '@hookform/resolvers/joi';
import { createFileRoute } from '@tanstack/react-router';
import { Typography, useNotification } from '@thisisdmg/tirecloud-patterns/v2';
import { type } from 'arktype';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import classes from './index.module.css';
import { clamp } from 'lodash-es';

export const Route = createFileRoute('/_main-ui/_protected/promotions/create')({
  component: PromotionCreatePage,
  validateSearch: type({
    s: type.string
      .or(type.number)
      .or(type.undefined)
      .pipe((s) =>
        match(s)
          .with(P.number, (x) => Math.max(0, x))
          .with(P.string, (x) => Math.max(0, ~~x))
          .otherwise(() => 0)
      )
      .default(0),
  }),
});

function PromotionCreatePage() {
  const createPromotionMutation = useCreatePromotionMutation();
  const notification = useNotification();
  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  const [currentStep, _setCurrentStep] = useState(0);
  const _stepFromSearch = Route.useSearch({ select: (s) => s.s });
  const [hasUserCompletedAllSteps, setHasUserCompletedAllSteps] =
    useState(false);

  useEffect(() => {
    updateLockStore.getState().lockUpdate();
    return () => updateLockStore.getState().unlockUpdate();
  }, []);

  const actualStepToUse = useMemo(() => {
    return hasUserCompletedAllSteps
      ? clamp(_stepFromSearch, 0, 4)
      : currentStep;
  }, [_stepFromSearch, currentStep, hasUserCompletedAllSteps]);

  useEffect(() => {
    if (currentStep === 4) {
      setHasUserCompletedAllSteps(true);
    }
  }, [currentStep, setHasUserCompletedAllSteps]);

  const setCurrentStep = useCallback(
    (updater: (prevStep: number) => number) => {
      const newStep = updater(actualStepToUse);
      _setCurrentStep(newStep);
      navigate({
        to: '/promotions/create',
        search: (prev) => ({ ...prev, s: newStep }),
        replace: true,
      });
    },
    [_setCurrentStep, navigate, actualStepToUse]
  );

  const promotionCreationFormValuesSchema = usePromotionFormValidationSchema();
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting, isValid, ...formState },
    ...form
  } = useForm<PromotionCreationFormValues>({
    mode: 'all',
    defaultValues: promotionCreationFormDefaultValues,
    shouldFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(promotionCreationFormValuesSchema, {
      abortEarly: false,
    }),
  });

  const submitForm: SubmitHandler<PromotionCreationFormValues> = useCallback(
    async (value) => {
      return createPromotionMutation
        .mutateAsync(value)
        .then(() => {
          notification.success({
            message: t('common.success'),
            description: t('promotions.promotion_created'),
          });
          navigate({
            to: '/promotions',
            search: {
              ref: 'promotion_create',
            },
          });
        })
        .catch((error) => {
          const e = castErrorToBaseError(error);
          notification.error({
            key: e.id,
            message: `0x${e.id.toString(16).toUpperCase()}`,
            description: t(`errors.gen.${e.path}`, { errorId: e.id }),
          });
        });
    },
    [createPromotionMutation, notification, navigate, t]
  );

  return (
    <>
      <Helmet>
        <title>{t('promotions.create_promotion')}</title>
      </Helmet>
      <div className={classes.promotion_create_page}>
        <div className={classes.header}>
          <Typography variant="hero">
            {t('promotions.create_promotion')}
          </Typography>
        </div>
        <FormProvider
          {...{
            ...form,
            control,
            watch,
            setValue,
            handleSubmit,
            trigger,
            formState: { ...formState, errors, isSubmitting, isValid },
            errors,
          }}
        >
          <PromotionForm
            onSubmit={submitForm}
            currentStep={actualStepToUse}
            setCurrentStep={setCurrentStep}
            isEditMode={false}
          />
        </FormProvider>
      </div>
    </>
  );
}
